import { QualityPrompt, QualityBrief } from './reviews/QualityContent'
/** Brief/prompt editing is shared by both portals; the server owns turn permissions. */
import { useEffect, useState } from 'react'
import { api, ApiError } from '@/api/client'
import { fmtDate } from '@/lib/format'
import { errorsFor, validate, type FieldError } from '@/lib/schema'
import type { ContentVersion, ContentHistoryPage, TaskDetail } from '@/lib/types'
import { BriefDownload } from './BriefDownload'
import { BriefForm, BriefView, editableBrief } from './BriefForm'
import { PromptWithChanges } from './ReviewChanges'
import { Section, Fold } from './Section'

const equal = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)
export function TaskContent({ data, onSaved, onDirty, otherDirty = false }: {
  data: TaskDetail; onSaved: (data: TaskDetail) => void; onDirty: (dirty: boolean) => void; otherDirty?: boolean
}) {
  const { task } = data
  const [editing, setEditing] = useState(false)
  const [base, setBase] = useState(task)
  const [brief, setBrief] = useState(() => editableBrief(task.brief))
  const [prompt, setPrompt] = useState(task.prompt)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [errors, setErrors] = useState<FieldError[]>([])
  const [conflict, setConflict] = useState(false)
  const [message, setMessage] = useState('')
  const canEdit = !!data.content_permissions?.can_edit
  const dirty = editing && (prompt !== base.prompt || !equal(brief, editableBrief(base.brief)))
  const stale = editing && (base.revision ?? 0) !== (task.revision ?? 0)

  useEffect(() => { onDirty(dirty || busy) }, [dirty, busy, onDirty])
  useEffect(() => () => onDirty(false), [onDirty])
  useEffect(() => {
    if (!dirty) return
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = '' }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  function start() {
    setBase(task); setBrief(editableBrief(task.brief)); setPrompt(task.prompt)
    setEditing(true); setError(''); setErrors([]); setConflict(false); setMessage('')
  }
  async function save() {
    const body = { revision: base.revision ?? 0,
      ...(prompt !== base.prompt ? { prompt } : {}),
      ...(!equal(brief, editableBrief(base.brief)) ? { brief } : {}),
    }
    const issues = validate(task.stage === 0 ? 'draft_content_update' : 'content_update', body)
    setErrors(issues)
    if (issues.length) return
    setBusy(true); setError('')
    try {
      const next = await api.put<TaskDetail>(`/tasks/${task.task_id}/content`, body)
      setEditing(false); setConflict(false); setMessage('Changes saved.'); onSaved(next)
    } catch (e) {
      setError((e as Error).message)
      if (e instanceof ApiError && (e.status === 409 || e.status === 403)) {
        setConflict(true)
        try { onSaved(await api.get<TaskDetail>(`/tasks/${task.task_id}`)) }
        catch { setError('Could not load the latest task. Your edits are kept; retry when connected.') }
      }
    } finally { setBusy(false) }
  }
  const needsReview = stale || conflict
  return <>
    <Section title="Agent prompt" aside={<><BriefDownload taskId={task.task_id} />{canEdit && !editing && <button className="sm" disabled={otherDirty} onClick={start}>Edit brief & prompt</button>}</>}>
      {editing ? <label className="field"><span>Agent prompt</span><textarea value={prompt} onChange={e => setPrompt(e.target.value)} disabled={busy} style={{ minHeight: 150 }} />
        {errorsFor(errors, 'prompt').map((message, i) => <span className="field-error" key={i}>{message}</span>)}
      </label> : data.quality_changes ? <QualityPrompt prompt={task.prompt} changes={data.quality_changes} /> : <PromptWithChanges task={task} label="Changes since draft submission" />}
    </Section>
    <Section title="Task brief">
      {editing ? <>
        <fieldset disabled={busy} className="content-fields"><BriefForm value={brief} onChange={setBrief} errors={errors} /></fieldset>
        {needsReview && <div className="note warn" role="alert">
          <p>The task changed. Your edits are kept above. Compare them with the latest saved content before saving again.</p>
          <Fold title="Latest saved content"><pre>{task.prompt}</pre><BriefView brief={task.brief} /></Fold>
          {canEdit && <button className="sm" onClick={() => { setBase(task); setConflict(false) }}>I’ve reviewed the latest content</button>}
        </div>}
        {error && <div className="note err" role="alert">{error}</div>}
        <div className="row editor-save">
          <button className="primary" disabled={busy || !dirty || !canEdit || needsReview || otherDirty} onClick={save}>{busy ? 'Saving…' : 'Save changes'}</button>
          <button disabled={busy} onClick={() => { setEditing(false); setError(''); setConflict(false); setErrors([]) }}>Cancel</button>
          {dirty && <span className="small muted">Unsaved changes</span>}
        </div>
      </> : <QualityBrief brief={task.brief} changes={data.quality_changes} />}
      {message && !editing && <p role="status" className="small muted">{message}</p>}
    </Section>
    <ContentHistory key={`${task.task_id}:${task.revision ?? 0}`} taskId={task.task_id} />
  </>
}

function ContentHistory({ taskId }: { taskId: string }) {
  const [history, setHistory] = useState<ContentHistoryPage | null>(null)
  const [version, setVersion] = useState<ContentVersion | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  async function load(cursor?: string) {
    setBusy(true); setError('')
    try {
      const next = await api.get<ContentHistoryPage>(`/tasks/${taskId}/content-history`, cursor ? { cursor } : undefined)
      setHistory(current => ({ ...next, versions: cursor ? [...(current?.versions ?? []), ...next.versions] : next.versions }))
    } catch (e) { setError((e as Error).message) }
    finally { setBusy(false) }
  }
  async function view(id: string) {
    setBusy(true); setError('')
    try { setVersion(await api.get<ContentVersion>(`/tasks/${taskId}/content-history/${id}`)) }
    catch (e) { setError((e as Error).message) }
    finally { setBusy(false) }
  }
  return <details className="fold content-history" onToggle={e => { if (e.currentTarget.open && !history && !busy) void load() }}>
    <summary>Version history</summary>
    <div className="fold-body">
      {history?.versions.map(item => <div className="row" key={item.version_id}>
        <button className="link small" disabled={busy} onClick={() => void view(item.version_id)}>{fmtDate(item.at)} · {item.actor.email}</button>
        <span className="small muted">{item.changed_fields.map(field => field === 'brief' ? 'Task brief' : 'Agent prompt').join(' & ')}</span>
      </div>)}
      {history?.versions.length === 0 && <p className="small muted">No saved content changes yet.</p>}
      {history?.cursor && <button className="sm" disabled={busy} onClick={() => void load(history.cursor!)}>Load older changes</button>}
      {busy && <p className="small muted">Loading…</p>}
      {error && <div role="alert" className="note err">{error}<button className="sm" onClick={() => void load()}>Retry</button></div>}
      {version && <div className="content-version"><p className="small muted">{fmtDate(version.at)} · {version.actor.email}</p>
        <Fold title="Before"><pre>{version.before.prompt}</pre><BriefView brief={version.before.brief} /></Fold>
        <Fold title="After"><pre>{version.after.prompt}</pre><BriefView brief={version.after.brief} /></Fold>
      </div>}
    </div>
  </details>
}
